import moment from "moment";
import {
  SpaceEvent,
  SpaceEventAction,
  SpaceEventReason,
  SpaceEventType,
  SpaceReservation,
  SpaceScanData,
  SpaceScanStatus,
  SpaceUser,
} from "../../../passapp-sdk/types/spaceTypes";
import { getInitials } from "../../../components/common/PL/Table/utils/getInitials";
import { ReservationIncomeItemData } from "../types/reservation.types";
import {
  RESERVATION_ACTION_LABELS,
  RESERVATION_RESULTS,
  ReservationResultAction,
  ReservationResultKey,
  ReservationResultNoticeData,
} from "../ReservationResult/reservationResults";
import { ReservationResultStatusVariant } from "../components/ReservationResultStatus/ReservationResultStatus";

const TIME_FORMAT = "HH:mm";

export const formatEventTime = (datetime?: string | null): string =>
  datetime ? moment(datetime).format(TIME_FORMAT) : "";

const REASON_DESCRIPTIONS: Record<SpaceEventReason, string> = {
  noReservation: "Sin reserva",
  outOfRange: "Fuera de horario",
  alreadyUsed: "Reserva ya utilizada",
  invalidQr: "QR no válido",
};

const TYPE_DESCRIPTIONS: Record<SpaceEventType, string> = {
  checkin: "Reserva válida",
  alternative: "Alternativa asignada",
  reject: "Ingreso denegado",
};

// La descripción explica el caso; qué se llevó lo dice la etiqueta de la fila.
export const describeSpaceEvent = (event: SpaceEvent): string => {
  if (event.reason) return REASON_DESCRIPTIONS[event.reason] || TYPE_DESCRIPTIONS[event.type];
  return TYPE_DESCRIPTIONS[event.type] || "";
};

/** Lo que se llevó (el snack o el plato), para la etiqueta de la fila. Al rechazado
 * no le corresponde: ahí la etiqueta tiene que seguir diciendo que no entró. */
export const eventOption = (event: SpaceEvent): string =>
  event.type === "reject" ? "" : event.optionName?.trim() || "";

export const userFullName = (user?: SpaceUser | null): string =>
  user ? `${user.firstname || ""} ${user.lastname || ""}`.trim() : "";

// Las iniciales no viajan en la respuesta: salen del nombre, igual que en el resto
// de los avatares de la app.
export const userInitials = (user?: SpaceUser | null): string =>
  getInitials(userFullName(user), "");

export const toIncomeItem = (event: SpaceEvent): ReservationIncomeItemData => ({
  id: event.id,
  name: userFullName(event.user),
  initials: userInitials(event.user),
  time: formatEventTime(event.datetime),
  type: event.type,
  option: eventOption(event),
  description: describeSpaceEvent(event),
});

export const todayForSpaceEvents = (): string => moment().format("YYYY-MM-DD");

const dayLabel = (datetime?: string | null): string => {
  if (!datetime) return "";
  const date = moment(datetime);
  return date.isSame(moment(), "day") ? "Hoy" : date.format("DD/MM");
};

const franjaLabel = (reservation?: SpaceReservation | null): string | undefined => {
  if (!reservation?.beginDatetime || !reservation?.endDatetime) return undefined;
  const begin = formatEventTime(reservation.beginDatetime);
  const end = formatEventTime(reservation.endDatetime);
  return `${dayLabel(reservation.beginDatetime)} · ${begin} a ${end}`;
};

const checkinLabel = (checkinAt?: string | null): string | undefined =>
  checkinAt ? `${dayLabel(checkinAt)} · ${formatEventTime(checkinAt)}` : undefined;

const resultDescription = (scan: SpaceScanData): string => {
  const { status, event, reservation } = scan;
  const scanTime = formatEventTime(event?.datetime) || moment().format(TIME_FORMAT);

  switch (status) {
    case "valid":
      return `Check-in registrado a las ${scanTime}`;
    case "outOfRange": {
      const begin = formatEventTime(reservation?.beginDatetime);
      const end = formatEventTime(reservation?.endDatetime);
      return begin && end
        ? `Son las ${scanTime}, su franja es ${begin} a ${end}`
        : `Son las ${scanTime}, llegó fuera de su franja`;
    }
    case "alreadyUsed":
      return reservation?.checkinAt
        ? `Esta reserva se usó a las ${formatEventTime(reservation.checkinAt)}`
        : "Esta reserva ya fue utilizada";
    case "noReservation":
      return "No figura reserva de comedor";
    default:
      return "No pudimos identificar a la persona";
  }
};

export type ReservationResultGuest = {
  name: string;
  initials: string;
  franja?: string;
  menu?: string;
  primerIngreso?: string;
};

export type ReservationResultView = {
  key: ReservationResultKey;
  variant: ReservationResultStatusVariant;
  title: string;
  description: string;
  guest: ReservationResultGuest | null;
  notice?: ReservationResultNoticeData;
  actions: ReservationResultAction[];
};

const action = (
  type: SpaceEventAction,
  label: string = RESERVATION_ACTION_LABELS[type]
): ReservationResultAction => ({ action: type, label });

// El escaneo no registra nada en estos casos: el operador decide y la elección queda
// registrada a su nombre. Las acciones son las que admite el servidor en `addSpaceEvent`.
// Con reserva fuera de horario le corresponde su menú (`checkin`). Con la reserva ya consumida
// no hay nada que consumir: al operador se le ofrece "Permitir acceso", pero lo que se manda
// es la alternativa. Sin reserva sólo queda asignar el snack.
const resultActions = (scan: SpaceScanData): ReservationResultAction[] => {
  if (!scan.user) return [];
  switch (scan.status) {
    case "outOfRange":
      return [action("reject"), action("checkin")];
    case "alreadyUsed":
      return [action("reject"), action("alternative", RESERVATION_ACTION_LABELS.checkin)];
    case "noReservation":
      return [action("reject"), { ...action("alternative"), icon: "snack" }];
    default:
      return [];
  }
};

export const buildReservationResult = (scan: SpaceScanData): ReservationResultView => {
  // Un status desconocido cae en la pantalla de QR no válido en lugar de romper.
  const copy = RESERVATION_RESULTS[scan.status] || RESERVATION_RESULTS.invalidQr;
  const name = userFullName(scan.user);

  return {
    key: scan.status,
    variant: copy.variant,
    title: copy.title,
    description: resultDescription(scan),
    guest: name
      ? {
          name,
          initials: userInitials(scan.user),
          franja: franjaLabel(scan.reservation),
          menu: scan.reservation?.optionName || scan.reservation?.optionLabel || undefined,
          primerIngreso:
            scan.status === "alreadyUsed" ? checkinLabel(scan.reservation?.checkinAt) : undefined,
        }
      : null,
    notice: copy.notice,
    actions: resultActions(scan),
  };
};
